import { useState, useEffect, useRef } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import stripeConfig from '../../config/stripe';

/**
 * Hook to handle native Google/Apple Pay triggers directly from buttons.
 * Detects if saved cards are available and manages the native sheet.
 */
export const useNativePayment = (currentUser, selectedPlan, hasUsedTrial, onSuccess, onError) => {
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [canMakePayment, setCanMakePayment] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  
  const fetchPromise = useRef(null);

  const fetchSecret = async () => {
    if (clientSecret) return clientSecret;
    if (fetchPromise.current) return fetchPromise.current;
    if (!currentUser || !selectedPlan) return null;

    setIsFetching(true);
    
    fetchPromise.current = (async () => {
      try {
        const createSubscriptionIntent = httpsCallable(functions, 'createSubscriptionIntent');
        const res = await createSubscriptionIntent({
          priceId: selectedPlan.priceId,
          userId: currentUser.uid,
          userEmail: currentUser.email,
          trialDays: hasUsedTrial ? 0 : (selectedPlan.trialDays || 0),
          mode: stripeConfig.mode
        });
        
        if (res.data?.clientSecret) {
          setClientSecret(res.data.clientSecret);
          return res.data.clientSecret;
        }
      } catch (err) {
        console.error('Secret fetch error handled:', err);
      } finally {
        setIsFetching(false);
        fetchPromise.current = null;
      }
      return null;
    })();

    return fetchPromise.current;
  };

  useEffect(() => {
    if (!stripe || !currentUser || !selectedPlan) return;

    const initPaymentRequest = async () => {
      const isTrial = !hasUsedTrial && (selectedPlan.trialDays > 0);
      const pr = stripe.paymentRequest({
        country: 'US',
        currency: 'usd',
        total: {
          label: `${selectedPlan.name}${isTrial ? ' (3 Days Free)' : ''}`,
          amount: isTrial ? 0 : 1999, // 0 for trial, 19.99 (cents) for direct
        },
        requestPayerName: true,
        requestPayerEmail: true,
        disableWallets: ['link'],
      });

      const result = await pr.canMakePayment();
      if (result) {
        setCanMakePayment(true);
        setPaymentRequest(pr);

        pr.on('paymentmethod', async (ev) => {
          try {
            // Await the promise if it's fetching, or perform a fresh fetch safely
            const activeSecret = await fetchSecret();
            
            if (!activeSecret) {
              ev.complete('fail');
              onError && onError('Failed to finalize secure connection. Please try again.');
              return;
            }

            const isSetupIntent = activeSecret?.startsWith('seti_');
            let result;

            if (isSetupIntent) {
              result = await stripe.confirmCardSetup(
                activeSecret,
                { payment_method: ev.paymentMethod.id },
                { handleActions: false }
              );
            } else {
              result = await stripe.confirmCardPayment(
                activeSecret,
                { payment_method: ev.paymentMethod.id },
                { handleActions: false }
              );
            }

            if (result.error) {
              ev.complete('fail');
              onError && onError(result.error.message);
            } else {
              ev.complete('success');
              // Critical: ensure onSuccess (navigation) happens immediately
              if (onSuccess) onSuccess();
            }
          } catch (err) {
            ev.complete('fail');
            onError && onError(err.message);
          }
        });
        
        setIsReady(true);
      } else {
        setCanMakePayment(false);
        setIsReady(true);
      }
    };

    initPaymentRequest();
  }, [stripe, currentUser, selectedPlan, hasUsedTrial]);

  const triggerNativePayment = async () => {
    if (paymentRequest && canMakePayment) {
      // Start fetching secret in background immediately, wait for ui
      fetchSecret();
      await paymentRequest.show();
      return true;
    }
    return false;
  };

  return {
    canMakePayment,
    triggerNativePayment,
    isReady,
    clientSecret,
    fetchSecret,
    isFetching
  };
};
